import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";

const HERO_IMG =
  "https://assets.bigcartel.com/theme_images/149113389/Publication+Instagram+Minimaliste+Citation+en+Beige+et+Gris++_22_.png?auto=format&fit=max&w=1400";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-2 -mb-2">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero({ onNavigate }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen overflow-hidden"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(212,175,55,0.09),transparent_55%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(156,78,58,0.07),transparent_50%)]" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-12 pt-36 pb-24 sm:pt-44 sm:pb-28 grid lg:grid-cols-12 gap-14 items-center">
        <motion.div style={{ opacity: fade }} className="lg:col-span-7">
          <motion.p
            data-testid="hero-eyebrow"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="text-xs uppercase tracking-[0.35em] font-mono text-[#C5A059] mb-8"
          >
            Maison de confection — France
          </motion.p>

          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-normal tracking-tight leading-[1.08]">
            <MaskedLine delay={0.35}>L'élégance du geste.</MaskedLine>
            <MaskedLine delay={0.5}>
              Le <em className="italic text-[#D4AF37]">raffinement</em>
            </MaskedLine>
            <MaskedLine delay={0.65}>de l'atelier.</MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-subheading"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 max-w-xl text-base sm:text-lg text-[#B0ABA0] leading-relaxed"
          >
            Kimonos, peignoirs, capes et tabliers d'exception pour salons de
            coiffure et espaces bien-être. Façonnés à la main en France, dans
            des matières qui traversent les années.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-collection"
              onClick={() => onNavigate("#collection")}
              className="group inline-flex items-center gap-3 rounded-full bg-[#D4AF37] px-7 py-3.5 text-sm font-semibold text-[#0C0D0E] transition-all duration-300 hover:bg-[#C5A059] hover:gap-4"
            >
              Découvrir la collection
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5" />
            </button>
            <button
              data-testid="hero-cta-devis"
              onClick={() => onNavigate("#contact")}
              className="inline-flex items-center gap-3 rounded-full border border-[#E2DBCB]/20 px-7 py-3.5 text-sm text-[#F7F5F0] transition-colors duration-300 hover:border-[#D4AF37]/60 hover:text-[#D4AF37]"
            >
              Demander un devis salon
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.35, duration: 1 }}
            className="mt-14 flex items-center gap-8 text-[11px] font-mono uppercase tracking-[0.2em] text-[#75726B]"
          >
            <img
              src="https://assets.bigcartel.com/assets/1249210/logo-made-in-france-1200-1200.jpg?w=240"
              alt="Logo Made in France"
              data-testid="hero-made-in-france-logo"
              className="h-10 w-10 rounded-full border border-[#D4AF37]/30 object-cover"
            />
            <span data-testid="hero-badge-handmade">Fait main en France</span>
            <span className="h-px w-8 bg-[#D4AF37]/40" />
            <span data-testid="hero-badge-fabric">Tissus déperlants sur demande</span>
            <span className="hidden sm:inline h-px w-8 bg-[#D4AF37]/40" />
            <span data-testid="hero-badge-embroidery" className="hidden sm:inline">Broderie personnalisée</span>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="absolute -inset-0 rounded-2xl border border-[#D4AF37]/25 translate-x-4 translate-y-4" />
            <div className="relative overflow-hidden rounded-2xl aspect-[4/5] bg-[#141619]">
              <motion.img
                src={HERO_IMG}
                alt="Kimono Tarantina sur mesure avec broderie personnalisée, présenté en salon"
                data-testid="hero-image"
                style={{ y: imgY, scale: 1.18 }}
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0C0D0E]/70 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#C5A059]">
                    Kimono sur mesure
                  </p>
                  <p className="font-serif text-lg text-[#F7F5F0]">
                    Broderie aux couleurs de votre salon
                  </p>
                </div>
                <span className="inline-flex items-center rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-3 py-1 text-[10px] font-mono uppercase tracking-widest text-[#D4AF37]">
                  Est. savoir-faire
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <motion.button
        data-testid="hero-scroll-cue"
        onClick={() => onNavigate("#manifeste")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#75726B] hover:text-[#D4AF37] transition-colors"
        style={{ animation: "float-slow 3s ease-in-out infinite" }}
        aria-label="Défiler vers le manifeste"
      >
        <ArrowDown size={18} />
      </motion.button>
    </section>
  );
}
